import React, { useState, useEffect } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import UserSideBar from "../../common/UserSidebar/UserSideBar";
import CalendarGrid from "./Calendar";
import { useTranslation } from "react-i18next";
import DetailPanel from "./DetailsPanel"; 
import { getCurrentUser } from "../../redux/userSlice";
import {
  formatDateRangeForZone,
  formatDateTimeForZone,
  getDateTimePartsForZone,
  parseBackendDate,
  USER_TIMEZONE,
} from "../../utils/dateUtils";

const DISPLAY_TIME_ZONE = USER_TIMEZONE;

const TherapyPlanner = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { currentUser } = useSelector((state) => state.users);

  useEffect(() => {
    dispatch(getCurrentUser())
  }, [dispatch]);

  const today = new Date();
  const todayDate = today.getDate();
  const todayMonth = today.getMonth();
  const todayYear = today.getFullYear();

  const [selectedDate, setSelectedDate] = useState(todayDate);
  const [currentMonth, setCurrentMonth] = useState(todayMonth);
  const [currentYear, setCurrentYear] = useState(todayYear);
  const [userRequests, setUserRequests] = useState([]);

  // CurrentUser'dan gelen requests'leri işle
  useEffect(() => {
    if (currentUser && currentUser.requests) {
      const processedRequests = currentUser.requests.map(request => {
        // availabilityIds varsa ve en az bir öğe içeriyorsa startDate'i kullan
        // yoksa createdAt'i kullan
        const slotInfo = request.availabilityIds && request.availabilityIds.length > 0
          ? request.availabilityIds[0]
          : null;

        const appointmentDateRaw = slotInfo
          ? parseBackendDate(slotInfo.startDate)
          : parseBackendDate(request.createdAt);

        const dateParts = getDateTimePartsForZone(appointmentDateRaw, DISPLAY_TIME_ZONE);
        const formattedDateTime = formatDateTimeForZone(appointmentDateRaw, DISPLAY_TIME_ZONE);
        const [datePart = "", timePart = ""] = formattedDateTime ? formattedDateTime.split(/\s+/) : [];

        const rawTimeRange = slotInfo?.yerelSaat || slotInfo?.secilenSaat || slotInfo?.timeRange;
        let displayTimeRange = "";
        if (rawTimeRange) {
          displayTimeRange = rawTimeRange
            .split("-")
            .map((segment) => segment.trim().replace(/\.$/, ""))
            .join(" - ");
        } else {
          const computedRange = formatDateRangeForZone(
            appointmentDateRaw,
            slotInfo?.endDate,
            DISPLAY_TIME_ZONE
          );
          if (computedRange) {
            displayTimeRange = datePart
              ? computedRange.replace(`${datePart} `, "").trim()
              : computedRange.split(" ").slice(1).join(" ");
          }
        }

        const rangeStart = displayTimeRange ? displayTimeRange.split(" - ")[0] : timePart;

        const day = dateParts ? parseInt(dateParts.day, 10) : appointmentDateRaw?.getDate();
        const month = dateParts ? parseInt(dateParts.month, 10) - 1 : appointmentDateRaw?.getMonth();
        const year = dateParts ? parseInt(dateParts.year, 10) : appointmentDateRaw?.getFullYear();
        const formattedDate = dateParts
          ? `${dateParts.day}.${dateParts.month}.${dateParts.year}`
          : datePart;
        const formattedTime = dateParts
          ? `${dateParts.hour}:${dateParts.minute}`
          : timePart;

        if (process.env.NODE_ENV !== "production") {
          console.debug("[TherapyPlanner] request time", {
            raw: request.availabilityIds?.[0]?.startDate,
            appointmentDateRaw,
            formattedDateTime,
            formattedDate,
            formattedTime,
            rawTimeRange,
            displayTimeRange,
          });
        }

        return {
          ...request,
          appointmentDate: appointmentDateRaw,
          date: day,
          month,
          year,
          dateKey: `${day}-${month}-${year}`,
          formattedDate,
          formattedTime: rangeStart || formattedTime,
          formattedTimeRange: displayTimeRange,
        };
      });
      setUserRequests(processedRequests);
    }
  }, [currentUser]);

  const handlePrevMonth = () => {
    if (currentMonth === 0) {
      setCurrentMonth(11);
      setCurrentYear(currentYear - 1);
    } else {
      setCurrentMonth(currentMonth - 1);
    }
  };

  const handleNextMonth = () => {
    if (currentMonth === 11) {
      setCurrentMonth(0);
      setCurrentYear(currentYear + 1);
    } else {
      setCurrentMonth(currentMonth + 1);
    }
  };

  // Takvimde tarihe tıklandığında sadece seçili tarihi güncelle
  const handleDateClick = (date) => {
    setSelectedDate(date);
  };

  // Request'leri olan günleri işaretle
  const getMarkedDates = () => {
    const markedDates = {};
    
    // Kullanıcının request'leri
    userRequests.forEach(request => {
      if (request.month === currentMonth && request.year === currentYear) {
        if (!markedDates[request.date]) {
          markedDates[request.date] = [];
        }
        markedDates[request.date].push({
          status: request.status,
          time: request.formattedTime,
          isUserRequest: true,
          requestId: request._id
        });
      }
    });
    
    return markedDates;
  };

  return (
    <div className="flex flex-col lg:flex-row w-full p-2 mt-[-20px]">
      <div className="flex-shrink-0">
        <UserSideBar />
      </div>

      <DetailPanel
        selectedDate={selectedDate}
        currentMonth={currentMonth}
        currentYear={currentYear}
        userRequests={userRequests}
        currentUser={currentUser}
      />

      <main className="w-full flex flex-col items-center order-2 lg:order-1">
        <div className="bg-white shadow-lg rounded-2xl p-4 sm:p-6 w-full max-w-[725px] h-auto md:h-[718px] flex flex-col">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-[36px] font-poppins font-bold">
              {`${t(
                `${new Date(currentYear, currentMonth).toLocaleString(
                  "en-US",
                  { month: "long" }
                )}`
              )} ${currentYear}`}
            </h2>
            <div className="flex space-x-2">
              <button
                onClick={handlePrevMonth}
                className="p-2 rounded-lg bg-gray-200"
              >
                <ChevronLeft size={20} />
              </button>
              <button
                onClick={handleNextMonth}
                className="p-2 rounded-lg bg-gray-200"
              >
                <ChevronRight size={20} />
              </button>
            </div>
          </div>

          <CalendarGrid
            currentYear={currentYear}
            currentMonth={currentMonth}
            selectedDate={selectedDate}
            setSelectedDate={setSelectedDate}
            onDateClick={handleDateClick}
            markedDates={getMarkedDates()}
            userRequests={userRequests}
          />
        </div>
      </main>
    </div>
  );
};

export default TherapyPlanner;
